import { ChevronRightOutlineIcon } from '@ikyucom/design-system-icons-react'
import { useCallback } from 'react'
import type { MouseEvent } from 'react'
import Image from '~/components/image/Image'
import { getFragmentData } from '~/lib/graphql'
import { Link, useReserveNavigate } from '~/navigation'
import { sva } from '~/styled-system/css'
import { flex, hstack, vstack } from '~/styled-system/patterns'
import { RestaurantPhotos } from '../RestaurantPhotos.frag'
import type { ImageThumbnailProps } from '../types'

const styles = sva({
  slots: ['container', 'titleContainer', 'title', 'count', 'chevron', 'ul', 'image'],
  base: {
    container: vstack.raw({
      alignItems: 'flex-start',
      gap: '3',
    }),
    titleContainer: {
      display: 'flex',
      alignItems: 'center',
    },
    title: {
      fontSize: '3xl',
      fontWeight: 'bold',
      lineHeight: 'normal',
    },
    count: {
      fontSize: 'sm',
      lineHeight: 'normal',
    },
    chevron: flex.raw({
      ml: '2',
      color: 'primary',
      width: '[20px]',
      height: '[20px]',
    }),
    ul: hstack.raw({
      gap: '3',
    }),
    image: {
      borderRadius: 'md',
    },
  },
})

export function ImageThumbnail({ restaurantId, restaurant }: ImageThumbnailProps) {
  const { images: _images, name: restaurantName } = getFragmentData(RestaurantPhotos, restaurant)
  const total = _images.length
  // PC では最大5枚
  const images = _images.slice(0, 5)

  const navigate = useReserveNavigate()
  const onClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      navigate({
        type: 'photos',
        imageIndex: Number(e.currentTarget.dataset.index),
      })
    },
    [navigate]
  )

  if (_images.length === 0) {
    return null
  }

  const classes = styles()
  return (
    <section className={classes.container}>
      <Link to={`/${restaurantId}/photos`} preventScrollReset={true}>
        <h3 className={classes.titleContainer}>
          <span className={classes.title}>写真</span>
          <span className={classes.count}>（{total}枚）</span>
          <ChevronRightOutlineIcon className={classes.chevron} />
        </h3>
      </Link>
      <ul className={classes.ul}>
        {images.map((image, index) => (
          <li key={image.url}>
            <button type="button" data-index={index} onClick={onClick}>
              <Image
                src={image.url}
                alt={`${restaurantName} - サムネイル${index + 1}`}
                className={classes.image}
                width={114}
                height={114}
                usePlaceholder={false}
              />
            </button>
          </li>
        ))}
      </ul>
    </section>
  )
}
